import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { usePatchCatalogSongMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem, CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { SONG_LYRICS_MAX_LENGTH } from '../../song/SongLyricsField';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { lyricLineCount } from '../addSongFlowSteps';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricsStepProps {
  accountId: number;
  item: CatalogItem;
  song: CatalogSong | undefined;
  eyebrow: string;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}

/**
 * Step 6, optional (design catalog-add-song i1-i4, owner answer 3): paste the lyrics.
 * `PATCH .../catalog/:uuid/song { lyrics }`. With a master, the server starts the lyric sync at once
 * (`queueLyricSync`); with no master, the sync starts when the master is stored.
 */
const LyricsStepComponent: React.FC<LyricsStepProps> = ({
  accountId,
  item,
  song,
  eyebrow,
  track,
  onBack,
  onNext,
  onSkip,
}) => {
  const intl = useIntl();
  const [lyrics, setLyrics] = useState(song?.lyrics ?? '');
  const [hasFailed, setFailed] = useState(false);
  const [patchSong, patching] = usePatchCatalogSongMutation();
  const hasMaster = song?.master !== null && song?.master !== undefined ? true : item.song.hasMaster;
  const trimmed = lyrics.trim();
  const isTooLong = lyrics.length > SONG_LYRICS_MAX_LENGTH;
  const lines = lyricLineCount(lyrics);
  let countText = intl.formatMessage(addSongFlowMessages.lyricsPlain);
  if (isTooLong) {
    countText = intl.formatMessage(addSongFlowMessages.lyricsTooLong, { max: SONG_LYRICS_MAX_LENGTH });
  } else if (lines > 0) {
    countText = intl.formatMessage(addSongFlowMessages.cardLyricsLines, { count: lines });
  }

  const save = useCallback(() => {
    if (isTooLong) {
      return;
    }
    // The step is optional: "Finish" with no lyrics ends the wizard, as "Skip for now" does (QA imp-5).
    if (trimmed === '') {
      onSkip();
      return;
    }
    if (trimmed === (song?.lyrics ?? '').trim()) {
      onNext();
      return;
    }
    setFailed(false);
    track(patchSong({ accountId, itemUuid: item.uuid, lyrics: trimmed }).unwrap())
      .then(() => onNext())
      .catch(() => setFailed(true));
  }, [accountId, isTooLong, item.uuid, onNext, onSkip, patchSong, song?.lyrics, track, trimmed]);

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(addSongFlowMessages.lyricsQuestion)}
        sub={intl.formatMessage(addSongFlowMessages.lyricsSub)}
      />
      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-song-lyrics">
          {intl.formatMessage(addSongFlowMessages.lyricsLabel)}{' '}
          <span className={styles.optional}>· {intl.formatMessage(addSongFlowMessages.optional)}</span>
        </label>
        <textarea
          id="add-song-lyrics"
          className={clsx(styles.input, styles.textarea, isTooLong && styles.inputBad)}
          value={lyrics}
          maxLength={SONG_LYRICS_MAX_LENGTH}
          placeholder={intl.formatMessage(addSongFlowMessages.lyricsPlaceholder)}
          disabled={patching.isLoading}
          onChange={(event) => setLyrics(event.target.value)}
        />
        <div className={clsx(styles.count, isTooLong && styles.countBad)}>
          <span>{countText}</span>
          <span>
            {lyrics.length} / {SONG_LYRICS_MAX_LENGTH}
          </span>
        </div>
      </div>
      {trimmed !== '' && (
        <div className={styles.alertInfo}>
          {intl.formatMessage(hasMaster ? addSongFlowMessages.lyricsWithAudio : addSongFlowMessages.lyricsNoAudio)}
        </div>
      )}
      {hasFailed && (
        <div className={styles.alertError} role="alert">
          {intl.formatMessage(addSongFlowMessages.lyricsSaveFailed)}
        </div>
      )}
      <StepActions
        onBack={onBack}
        onSkip={onSkip}
        main={{
          label: intl.formatMessage(addSongFlowMessages.finish),
          onClick: save,
          disabled: isTooLong,
          isLoading: patching.isLoading,
          testID: 'add-song-lyrics-finish',
        }}
      />
    </>
  );
};

export const LyricsStep = TypedMemo(LyricsStepComponent);
